/*
 * Copyright 2022 Siemens
 */

import React from 'react';
import {DatastoreItem, Datastores} from '../services/datastores';
import {
  SwwcAdvancedSearch,
  SwwcButton,
  SwwcCommandDropdown,
  SwwcCommandDropdownItem,
  SwwcCommandPipe,
  SwwcDefaultCell,
  SwwcDropdown,
  SwwcFileUpload,
  SwwcIcon,
  SwwcInputField,
  SwwcLabel,
  SwwcSearchBox,
  SwwcTextarea,
} from '@com.siemens.plm.horizon/swwc/bindings/react';
import {CommandBar, type CommandBarProps} from './commandbar';
import App from '../App';
import {SWWCDropDownMenuOptionItem} from '@com.siemens.plm.horizon/swwc/dist/types/components/swwc-dropdown/src/types';
import {Settings} from '../services/settings';
import {SetOptional, SetRequired} from 'type-fest';
import {Communication} from '../services/communication';
import {Panel} from './panel';
import {Observer} from '../shared/observer';
import {Information} from './information';

interface DatastoreProps extends SetRequired<CommandBarProps, 'reloadCommand'> {
  type: 'Experiment' | 'Job' | 'Parameter set';
  items?: ReadonlyArray<DatastoreItem>;
}

interface DatastoreEnvironment {
  env: ReadonlyArray<{key: string; value: string; changed: boolean}>;
  newEnv: Array<{key: string; value: string; index: number}>;
}

interface DatastoreState {
  items: Array<DatastoreItem>;

  form?: (SetOptional<DatastoreItem, keyof DatastoreItem> & DatastoreEnvironment) | undefined;
  formReadOnly: boolean;
  formSubmission: boolean;
  formUploading: boolean;
  selected?: (DatastoreItem & DatastoreEnvironment) | undefined;

  defaultImage?: SWWCDropDownMenuOptionItem | undefined;
  images: Array<SWWCDropDownMenuOptionItem>;
  defaultNode?: SWWCDropDownMenuOptionItem | undefined;
  nodes: Array<SWWCDropDownMenuOptionItem>;

  experiments?: Array<SWWCDropDownMenuOptionItem & Pick<DatastoreItem, 'status' | 'statusState'>>;
  parameters?: Array<SWWCDropDownMenuOptionItem & Pick<DatastoreItem, 'status' | 'statusState'>>;

  sorted?: 'name:asc' | 'name:desc' | undefined;
  filtered: Array<string>;
  searched?: string | undefined;
}

export class Datastore extends React.Component<DatastoreProps, DatastoreState> {
  readonly #symbol = Symbol('Datastore');
  readonly #fileUpload: React.Ref<HTMLSwwcFileUploadElement>;
  readonly #fileUploadStatus = new Observer<{done: boolean; url: string}>();

  #panelFields: {
    datastore?: string;
    stateCreate?: SWWCDropDownMenuOptionItem[];
    state: SWWCDropDownMenuOptionItem[];
    node?: true;
    template?: true;
    templateType?: 'build' | 'run';
    message?: true;
    environment?: true;
    parameter?: true;
    experiment?: true;
    upload?: true;
    extraResultFiles?: true;
  };

  constructor(props: DatastoreProps, state: DatastoreState) {
    super(props, state);
    this.#fileUpload = React.createRef();
    this.state =
      typeof state === 'object'
        ? {
            ...state,
            formReadOnly: true,
            formSubmission: false,
            formUploading: false,
            images: [],
            nodes: [],
            filtered: [],
            items: [],
          }
        : {
            formReadOnly: true,
            formSubmission: false,
            formUploading: false,
            images: [],
            nodes: [],
            filtered: [],
            items: [],
          };
    switch (props.type) {
      case 'Experiment':
        this.#panelFields = {
          datastore: 'experiment',
          environment: true,
          template: true,
          templateType: 'build',
          upload: true,
          message: true,
          state: [
            {label: 'New', value: 'new'},
            {label: 'Ready', value: 'ready'},
            {label: 'Terminate', value: 'terminate'},
          ],
          stateCreate: [
            {label: 'New', value: 'new'},
            {label: 'Ready', value: 'ready'},
          ],
        };
        break;
      case 'Parameter set':
        this.#panelFields = {
          datastore: 'parameterset',
          upload: true,
          state: [
            {label: 'New', value: 'new'},
            {label: 'Ready', value: 'ready'},
          ],
        };
        break;
      case 'Job':
        this.#panelFields = {
          datastore: 'job',
          node: true,
          template: true,
          templateType: 'run',
          parameter: true,
          experiment: true,
          environment: true,
          message: true,
          extraResultFiles: true,
          state: [
            {label: 'New', value: 'new'},
            {label: 'Ready', value: 'ready'},
            {label: 'Terminate', value: 'terminate'},
          ],
          stateCreate: [
            {label: 'New', value: 'new'},
            {label: 'Ready', value: 'ready'},
          ],
        };
        break;
      default:
        this.#panelFields = {
          state: [],
        };
        break;
    }
  }

  override componentDidMount() {
    if (super.componentDidMount) super.componentDidMount();
    if (this.props.items !== undefined) {
      const params = App.pageParam;
      if ('id' in params && typeof params.id === 'string') {
        let eleId = params.id;
        let foundEle = this.props.items.find((ele) => eleId === ele.id);
        eleId = eleId.toLocaleLowerCase();
        if (foundEle === undefined) foundEle = this.props.items.find((ele) => ele.name.toLocaleLowerCase() === eleId);
        if (foundEle === undefined) {
          const foundEles = this.props.items.filter((ele) => ele.name.toLocaleLowerCase().includes(eleId));
          console.debug(this.props.type, 'found element count: ', foundEles.length);
          foundEle = foundEles.at(0);
        }
        if (foundEle === undefined)
          App.toast('error', this.props.type, undefined, `${this.props.type} is not found [ID: '${App.pageParam.id}']`);
        else {
          const selected = {
            ...foundEle,
            env:
              foundEle.environment === undefined
                ? []
                : Object.entries(foundEle.environment).map((ele) => ({
                    key: ele[0],
                    value: ele[1],
                    changed: false,
                  })),
            newEnv: [],
          };
          this.setState({
            selected,
            form: selected,
            formReadOnly: !(
              'action' in params &&
              typeof params.action === 'string' &&
              params.action.toLocaleLowerCase() === 'edit'
            ),
          });
        }
      }
      this.#storeUpdate();
    }
    Datastores.resourceOption.addObserver(this.#symbol, (option) => {
      if (option === null) {
        this.setState({
          images: [],
          defaultImage: undefined,
          nodes: [],
          defaultNode: undefined,
        });
      } else {
        const images = option.image.map((ele) => ({
          label: ele.name,
          value: ele.id,
          desc: `Prescan: ${ele.prescan}`,
        }));
        const nodes = option.node.map((ele) =>
          ele.description === undefined
            ? {label: ele.name, value: ele.id}
            : {
                label: ele.name,
                value: ele.id,
                desc: ele.description,
              },
        );
        this.setState({
          images,
          nodes,
          // TODO: We need to consider workspace default options for defaultImage or defaultNode
          defaultImage: images.find((image) => image.label.toLocaleLowerCase() === 'default'),
          defaultNode: nodes.find((image) => image.label.toLocaleLowerCase() === 'default'),
        });
      }
    });
    if (this.#panelFields.experiment) {
      Datastores.experimentItems.addObserver(this.#symbol, (value) => {
        this.setState({
          experiments:
            value === null
              ? [{label: '<EMPTY>', value: '', status: '', statusState: 'default'}]
              : [
                  {label: '<EMPTY>', value: '', status: '', statusState: 'default'},
                  ...value.map((ele) => ({
                    label: ele.name,
                    value: ele.id,
                    desc: `ID: ${ele.id} Status: ${ele.status}`,
                    status: ele.status,
                    statusState: ele.statusState,
                  })),
                ],
        });
      });
    }
    if (this.#panelFields.parameter) {
      Datastores.parameterSetItems.addObserver(this.#symbol, (value) => {
        this.setState({
          parameters:
            value === null
              ? [{label: '<EMPTY>', value: '', status: '', statusState: 'default'}]
              : [
                  {label: '<EMPTY>', value: '', status: '', statusState: 'default'},
                  ...value.map((ele) => ({
                    label: ele.name,
                    value: ele.id,
                    desc: `ID: ${ele.id} Status: ${ele.status}`,
                    status: ele.status,
                    statusState: ele.statusState,
                  })),
                ],
        });
      });
    }
  }

  override componentDidUpdate(prevProps: Readonly<DatastoreProps>, prevState: Readonly<DatastoreState>, snapshot?: any) {
    if (super.componentDidUpdate) super.componentDidUpdate(prevProps, prevState, snapshot);
    if (this.props.items !== undefined && this.props.items !== prevProps.items) {
      this.#storeUpdate();
      if (this.state.selected !== undefined) {
        const foundEle = this.props.items.find((ele) => ele.id === this.state.selected!.id);
        const selected =
          foundEle === undefined
            ? undefined
            : {
                ...foundEle,
                env:
                  foundEle.environment === undefined
                    ? []
                    : Object.entries(foundEle.environment).map((ele) => ({
                        key: ele[0],
                        value: ele[1],
                        changed: false,
                      })),
                newEnv: [],
              };
        this.setState(this.state.formReadOnly && !this.state.formSubmission ? {selected, form: selected} : {selected});
      }
    }
  }

  override componentWillUnmount() {
    if (this.#panelFields.parameter) Datastores.parameterSetItems.removeObserver(this.#symbol);
    if (this.#panelFields.experiment) Datastores.experimentItems.removeObserver(this.#symbol);
    Datastores.resourceOption.removeObserver(this.#symbol);
    if (super.componentWillUnmount) super.componentWillUnmount();
  }

  #panelShow(item: DatastoreItem, isEdit?: true): void {
    const selected = {
      ...item,
      env:
        item.environment === undefined
          ? []
          : Object.entries(item.environment).map((ele) => ({
              key: ele[0],
              value: ele[1],
              changed: false,
            })),
      newEnv: [],
    };
    this.setState({
      selected,
      form: isEdit ? JSON.parse(JSON.stringify(selected)) : selected,
      formReadOnly: !isEdit,
      formSubmission: false,
      formUploading: false,
    });
    App.navigate(`/${App.pageName}/${item.id}${isEdit ? '/edit' : ''}`);
  }

  #panelHide(deleteItem?: DatastoreItem): void {
    this.setState({
      form: undefined,
      selected: undefined,
      formReadOnly: false,
      formSubmission: false,
      formUploading: false,
    });
    App.navigate(`/${App.pageName}`);

    if (deleteItem) {
      App.modal(
        'error',
        this.props.type,
        `Are you sure you want to delete "${deleteItem.name}"?`,
        (action) => {
          if (action !== 'yes') return;

          Communication.fetch(
            {workspace: true, datastore: this.#panelFields.datastore, resource: deleteItem.id ?? deleteItem.name},
            'DELETE',
            'json+object',
          ).then(
            (res) => {
              if (res.status === 'ok') {
                App.toast(
                  'info',
                  this.props.type,
                  undefined,
                  `${this.props.type} ${deleteItem.name} is deleted [ID: ${deleteItem.id}]`,
                );
              } else {
                App.toast(
                  'error',
                  this.props.type,
                  undefined,
                  `Failed to delete ${this.props.type} ${deleteItem.name} [ID: ${deleteItem.id}]: ${
                    'data' in res ? (typeof res.data === 'string' ? res.data : JSON.stringify(res.data)) : res.error.message
                  }`,
                );
              }
            },
            (val) => {
              App.toast(
                'error',
                this.props.type,
                undefined,
                `Unknown error during deletion of ${this.props.type} ${deleteItem.name} [ID: ${deleteItem.id}]: ${val}`,
              );
            },
          );
        },
        'Delete',
        'Cancel',
      );
    }
  }

  #storeUpdate(action?: 'sort' | 'filter' | 'search', name?: string): void {
    const newState: Partial<DatastoreState> = {};
    switch (action) {
      case 'sort':
        if (this.state.sorted === name) newState.sorted = undefined;
        else newState.sorted = name as DatastoreState['sorted'];
        break;
      case 'filter':
        if (name) {
          newState.filtered = this.state.filtered.slice();
          if (newState.filtered.includes(name)) newState.filtered.splice(newState.filtered.indexOf(name), 1);
          else newState.filtered.push(name);
        } else newState.filtered = [];
        break;
      case 'search':
        if (name === undefined || name.length === 0) newState.searched = undefined;
        else newState.searched = name.toLocaleLowerCase();
        break;
      default:
        break;
    }
    const filter = newState.filtered ?? this.state.filtered;
    if (this.props.items === undefined) newState.items = [];
    else if (filter.length === 0) newState.items = this.props.items.slice();
    else newState.items = this.props.items.filter((item) => filter.includes(item.status));
    if (newState.searched !== undefined)
      newState.items = newState.items.filter(
        (ele) =>
          ele.id.toLocaleLowerCase().includes(newState.searched!) ||
          ele.name.toLocaleLowerCase().includes(newState.searched!) ||
          `${ele.desc === undefined ? '' : ele.desc}`.toLocaleLowerCase().includes(newState.searched!),
      );
    if (newState.sorted !== undefined) newState.items.sort((a, b) => a.name.localeCompare(b.name));
    if (newState.sorted === 'name:desc') newState.items.reverse();
    this.setState(newState as DatastoreState);
  }

  override render() {
    const panelIsCreation = this.state.form === undefined || this.state.form.id === undefined;
    const panelIsReadOnly =
      this.state.formReadOnly || (this.state.selected !== undefined && this.state.selected.status !== 'new');
    return (
      <React.Fragment>
        <CommandBar
          addCommand={() => {
            this.setState({
              form: {status: 'new', env: [], newEnv: []},
              selected: undefined,
              formReadOnly: false,
              formSubmission: false,
            });
            App.navigate(`/${App.pageName}`);
          }}
          deleteAllCommand={() => {
            App.modal(
              'error',
              this.props.type,
              'Are you sure you want to delete all?',
              (action) => {
                if (action !== 'yes') return;

                this.#panelHide();
                Communication.fetch(
                  {
                    workspace: true,
                    datastore: this.#panelFields.datastore,
                    isResources: true,
                  },
                  'DELETE',
                  'json+object',
                ).then(
                  (res) => {
                    if (res.status === 'ok') {
                      App.toast('info', this.props.type, undefined, `All ${this.props.type}s are deleted`);
                    } else {
                      App.toast(
                        'error',
                        this.props.type,
                        undefined,
                        `Failed to delete all ${this.props.type}s: ${
                          'data' in res
                            ? typeof res.data === 'string'
                              ? res.data
                              : JSON.stringify(res.data)
                            : res.error.message
                        }`,
                      );
                    }
                  },
                  (val) => {
                    App.toast(
                      'error',
                      this.props.type,
                      undefined,
                      `Unknown error during delete all ${this.props.type}s: ${val}`,
                    );
                  },
                );
              },
              'Delete all',
              'Cancel',
            );
          }}
          reloadCommand={this.props.reloadCommand}
        >
          <SwwcAdvancedSearch>
            <SwwcSearchBox
              slot='search-box'
              disabled={this.props.items === undefined || this.props.items.length === 0}
              onValueChanged={(event) => this.#storeUpdate('search', event.detail.value)}
              placeholder={`Search ${this.props.type}`}
            />
          </SwwcAdvancedSearch>
          <SwwcCommandPipe />
          <SwwcCommandDropdown
            icon='cmdSort16'
            label='Sort'
          >
            <SwwcCommandDropdownItem
              icon={this.state.sorted === 'name:asc' ? 'cmdSortAtoZ16' : ''}
              label='Name (Ascending)'
              onClickEvent={() => this.#storeUpdate('sort', 'name:asc')}
            />
            <SwwcCommandDropdownItem
              icon={this.state.sorted === 'name:desc' ? 'cmdSortZtoA16' : ''}
              label='Name (Descending)'
              onClickEvent={() => this.#storeUpdate('sort', 'name:desc')}
            />
            <SwwcCommandDropdownItem
              disabled={this.state.sorted === undefined}
              icon='cmdTrash16'
              label='Clear sorting'
              onClickEvent={() => this.#storeUpdate('sort', undefined)}
            />
          </SwwcCommandDropdown>
          <SwwcCommandDropdown
            icon='cmdFilter16'
            label='Filter'
          >
            <SwwcCommandDropdownItem
              icon={this.state.filtered.includes('new') ? 'cmdFilter16' : ''}
              label='Status is New'
              onClickEvent={() => this.#storeUpdate('filter', 'new')}
            />
            <SwwcCommandDropdownItem
              icon={this.state.filtered.includes('ready') ? 'cmdFilter16' : ''}
              label='Status is Ready'
              onClickEvent={() => this.#storeUpdate('filter', 'ready')}
            />
            {this.#panelFields.template && (
              <React.Fragment>
                <SwwcCommandDropdownItem
                  icon={this.state.filtered.includes('terminate') ? 'cmdFilter16' : ''}
                  label='Status is Terminate'
                  onClickEvent={() => this.#storeUpdate('filter', 'terminate')}
                />
                <SwwcCommandDropdownItem
                  icon={this.state.filtered.includes('terminating') ? 'cmdFilter16' : ''}
                  label='Status is Terminating'
                  onClickEvent={() => this.#storeUpdate('filter', 'terminating')}
                />
                <SwwcCommandDropdownItem
                  icon={this.state.filtered.includes('terminated') ? 'cmdFilter16' : ''}
                  label='Status is Terminated'
                  onClickEvent={() => this.#storeUpdate('filter', 'terminated')}
                />
                <SwwcCommandDropdownItem
                  icon={this.state.filtered.includes('failed') ? 'cmdFilter16' : ''}
                  label='Status is Failed'
                  onClickEvent={() => this.#storeUpdate('filter', 'failed')}
                />
              </React.Fragment>
            )}
            {this.#panelFields.templateType === 'build' && (
              <React.Fragment>
                <SwwcCommandDropdownItem
                  icon={this.state.filtered.includes('building') ? 'cmdFilter16' : ''}
                  label='Status is Building'
                  onClickEvent={() => this.#storeUpdate('filter', 'building')}
                />
                <SwwcCommandDropdownItem
                  icon={this.state.filtered.includes('built') ? 'cmdFilter16' : ''}
                  label='Status is Built'
                  onClickEvent={() => this.#storeUpdate('filter', 'built')}
                />
              </React.Fragment>
            )}
            {this.#panelFields.templateType === 'run' && (
              <React.Fragment>
                <SwwcCommandDropdownItem
                  icon={this.state.filtered.includes('running') ? 'cmdFilter16' : ''}
                  label='Status is Running'
                  onClickEvent={() => this.#storeUpdate('filter', 'running')}
                />
                <SwwcCommandDropdownItem
                  icon={this.state.filtered.includes('finished') ? 'cmdFilter16' : ''}
                  label='Status is Finished'
                  onClickEvent={() => this.#storeUpdate('filter', 'finished')}
                />
              </React.Fragment>
            )}
            <SwwcCommandDropdownItem
              disabled={this.state.filtered.length === 0}
              icon='cmdTrash16'
              label='Clear all status'
              onClickEvent={() => this.#storeUpdate('filter', undefined)}
            />
          </SwwcCommandDropdown>
        </CommandBar>
        <div className='paneled'>
          <div className='content'>
            {this.state.items.length === 0 && (
              <SwwcLabel
                label={this.props.type}
                status='default'
              >
                <span slot='content'>No data available</span>
              </SwwcLabel>
            )}
            {this.state.items.map(
              (
                item, // TODO: Migrate to table component when possible if the rendering is good
              ) => (
                <SwwcDefaultCell
                  key={item.id}
                  cellTitle={item.name}
                  subTitle={item.id}
                  selected={this.state.selected === item}
                  onClick={() => this.#panelShow(item)}
                >
                  <div slot='right-column-area'>
                    <SwwcIcon
                      name='cmdEdit16'
                      size='lg'
                      title='Edit'
                      onClick={(event: {stopPropagation: () => void}) => {
                        event.stopPropagation();
                        this.#panelShow(item, true);
                      }}
                    ></SwwcIcon>
                    <SwwcIcon
                      name='cmdTrash16'
                      size='lg'
                      title='Delete'
                      onClick={(event: {stopPropagation: () => void}) => {
                        event.stopPropagation();
                        this.#panelHide(item);
                      }}
                    ></SwwcIcon>
                  </div>
                  <div slot='bottom-area'>
                    <SwwcLabel
                      label='Status'
                      status={item.statusState!}
                    >
                      <span slot='content'>{item.status}</span>
                    </SwwcLabel>
                  </div>
                </SwwcDefaultCell>
              ),
            )}
          </div>
          {this.state.form && (
            <Panel
              title={panelIsCreation ? `New ${this.props.type}` : `${this.props.type}: ${this.state.form.name}`}
              isReadOnly={this.state.formReadOnly}
              isSubmission={this.state.formSubmission || this.state.formUploading}
              onClose={() => this.#panelHide()}
              onDelete={() => this.#panelHide(this.state.selected!)}
              onEdit={() => this.#panelShow(this.state.selected!, true)}
              onSubmitCancel={() => {
                if (this.state.selected === undefined) this.#panelHide();
                else this.#panelShow(this.state.selected);
              }}
              onSubmit={() => {
                this.setState({formReadOnly: true, formSubmission: true});
                const environment: Record<string, string> = {};
                if (this.state.form) {
                  for (const ele of this.state.form.env) if (ele.changed) environment[ele.key] = ele.value;
                  for (const ele of this.state.form.newEnv) if (ele.key.length > 0) environment[ele.key] = ele.value;
                }
                Communication.fetch<DatastoreItem, false>(
                  panelIsCreation
                    ? {
                        workspace: true,
                        datastore: this.#panelFields.datastore,
                        isResources: true,
                      }
                    : {workspace: true, datastore: this.#panelFields.datastore, resource: this.state.form!.id},
                  panelIsCreation ? 'POST' : 'PATCH',
                  'json+object',
                  panelIsCreation && this.props.type !== 'Job'
                    ? {
                        ...this.state.form,
                        status: 'new',
                        env: undefined,
                        environment,
                      }
                    : {...this.state.form, env: undefined, environment},
                ).then(
                  (res) => {
                    if (res.status === 'ok') {
                      if (
                        this.state.form !== undefined &&
                        this.state.form !== this.state.selected &&
                        this.state.form.name === res.data.name
                      ) {
                        if (
                          panelIsCreation &&
                          this.#fileUpload !== null &&
                          'current' in this.#fileUpload &&
                          this.#fileUpload.current !== null &&
                          this.#fileUpload.current.manualUpload
                        ) {
                          this.#fileUploadStatus.addObserver(this.#symbol, (status) => {
                            if (status.done)
                              this.#panelShow(this.props.items?.find((ele) => ele.name === res.data.name) ?? res.data);
                            else
                              App.toast(
                                'error',
                                this.props.type,
                                undefined,
                                `File upload failure for resource "${res.data.name}"`,
                              );
                            this.#fileUploadStatus.removeAllObservers();
                          });
                          this.#fileUpload.current.startUpload().then(
                            (files) => {
                              if (files === 0)
                                this.#panelShow(this.props.items?.find((ele) => ele.name === res.data.name) ?? res.data);
                            },
                            () => {
                              App.toast(
                                'error',
                                this.props.type,
                                undefined,
                                `File upload during resource "${res.data.name}" creation failed`,
                              );
                              this.#fileUploadStatus.removeAllObservers();
                            },
                          );
                        } else this.#panelShow(this.props.items?.find((ele) => ele.name === res.data.name) ?? res.data);
                      } else
                        App.toast('info', this.props.type, undefined, `Submission of resource "${res.data.name}" is completed`);
                    } else {
                      App.toast(
                        'error',
                        this.props.type,
                        undefined,
                        'data' in res
                          ? typeof res.data === 'string'
                            ? res.data
                            : JSON.stringify(res.data)
                          : res.error.message,
                      );
                      if (this.state.form !== undefined && this.state.form !== this.state.selected) {
                        this.setState({formReadOnly: false, formSubmission: false});
                      }
                    }
                  },
                  (val) => {
                    App.toast(
                      'error',
                      this.props.type,
                      undefined,
                      `Unknown error during ${panelIsCreation ? 'creation' : 'updating'} of resource: ${val}`,
                    );
                    if (this.state.form !== undefined && this.state.form !== this.state.selected) {
                      this.setState({formReadOnly: false, formSubmission: false});
                    }
                  },
                );
              }}
            >
              <SwwcInputField
                name='id'
                label='Identifier'
                value={this.state.form.id ?? ''}
                readonly={true}
              />
              <SwwcInputField
                name='name'
                autofocus={true}
                label='Name'
                charLength={this.state.form.name?.length ?? 0}
                value={this.state.form.name ?? ''}
                onValueChanged={(val) => (this.state.form!.name = val.detail.newValue)}
                readonly={panelIsReadOnly}
                errorMessage='Name is required'
                required={true}
                minlength={1}
                maxlength={Settings.configuration.limit.param}
              />
              <SwwcInputField
                name='desc'
                autofocus={true}
                label='Description'
                value={this.state.form.desc ?? ''}
                onValueChanged={(val) => (this.state.form!.desc = val.detail.newValue)}
                readonly={panelIsReadOnly}
              />
              {this.state.formReadOnly ? (
                <SwwcInputField
                  label='Status'
                  value={this.state.form.status ?? 'new'}
                  readonly={true}
                />
              ) : (
                <React.Fragment>
                  <SwwcLabel label='Status' />
                  <SwwcDropdown
                    initialValue={this.state.form.status ?? 'new'}
                    onValueChange={(val) => (this.state.form!.status = val.detail.value[0])}
                    options={
                      panelIsCreation && this.#panelFields.stateCreate !== undefined
                        ? this.#panelFields.stateCreate
                        : this.#panelFields.state
                    }
                    selectionMode='single'
                  />
                </React.Fragment>
              )}
              {this.#panelFields.message && (
                <SwwcTextarea
                  label='Message'
                  value={this.state.form.result ?? ''}
                  readonly={true}
                />
              )}
              {this.#panelFields.experiment &&
                this.state.experiments &&
                this.state.experiments.length > 0 &&
                (panelIsReadOnly ? (
                  this.state.form.experimentId === undefined ? (
                    <SwwcInputField
                      label='Experiment'
                      value='No selected experiment'
                      readonly={true}
                    />
                  ) : (
                    <React.Fragment>
                      <SwwcLabel label='Experiment' />
                      <SwwcDefaultCell
                        cellTitle={this.state.form.experiment ?? ''}
                        subTitle={this.state.form.experimentId}
                        grayish={true}
                        selected={false}
                      >
                        <div slot='bottom-area'>
                          <SwwcLabel
                            label='Status'
                            status={
                              this.state.experiments.find((ele) => ele.value === this.state.form!.experimentId)?.statusState ??
                              'error'
                            }
                          >
                            <span slot='content'>
                              {this.state.experiments.find((ele) => ele.value === this.state.form!.experimentId)?.status ??
                                '<UNKNOWN>'}
                            </span>
                          </SwwcLabel>
                          <SwwcLabel label='Selected data file'>
                            <span slot='content'>{this.state.form.selectedExperimentDataFile ?? 'N/A'}</span>
                          </SwwcLabel>
                        </div>
                        <div slot='right-column-area'>
                          <SwwcIcon
                            name='cmdAddPart24'
                            size='lg'
                            title={`Experiment: ${this.state.form.experiment}`}
                            onClick={() => App.navigate(`/exp/${this.state.form!.experimentId}`)}
                          ></SwwcIcon>
                        </div>
                      </SwwcDefaultCell>
                    </React.Fragment>
                  )
                ) : (
                  <React.Fragment>
                    <SwwcLabel label='Experiment' />
                    <SwwcDropdown
                      initialValue={this.state.form.experimentId ?? ''}
                      onValueChange={(val) => (this.state.form!.experimentId = val.detail.value[0])}
                      options={this.state.experiments}
                      selectionMode='single'
                    />
                  </React.Fragment>
                ))}
              {this.#panelFields.experiment &&
                this.state.parameters &&
                this.state.parameters.length > 0 &&
                (panelIsReadOnly ? (
                  this.state.form.parametersetId === undefined ? (
                    <SwwcInputField
                      label='Parameter Set'
                      value='No selected parameter set'
                      readonly={true}
                    />
                  ) : (
                    <React.Fragment>
                      <SwwcLabel label='Parameter Set' />
                      <SwwcDefaultCell
                        cellTitle={this.state.form.parameterset ?? ''}
                        subTitle={this.state.form.parametersetId}
                        grayish={true}
                        selected={false}
                      >
                        <div slot='bottom-area'>
                          <SwwcLabel
                            label='Status'
                            status={
                              this.state.parameters.find((ele) => ele.value === this.state.form!.parametersetId)?.statusState ??
                              'error'
                            }
                          >
                            <span slot='content'>
                              {this.state.parameters.find((ele) => ele.value === this.state.form!.parametersetId)?.status ??
                                '<UNKNOWN>'}
                            </span>
                          </SwwcLabel>
                          <SwwcLabel label='Selected data file'>
                            <span slot='content'>{this.state.form.selectedParametersetDataFile ?? 'N/A'}</span>
                          </SwwcLabel>
                        </div>
                        <div slot='right-column-area'>
                          <SwwcIcon
                            name='cmdRecordPartMovement24'
                            size='lg'
                            title={`Parameter set: ${this.state.form.parameterset}`}
                            onClick={() => App.navigate(`/pms/${this.state.form!.parametersetId}`)}
                          ></SwwcIcon>
                        </div>
                      </SwwcDefaultCell>
                    </React.Fragment>
                  )
                ) : (
                  <React.Fragment>
                    <SwwcLabel label='Parameter Set' />
                    <SwwcDropdown
                      initialValue={this.state.form.parametersetId ?? ''}
                      onValueChange={(val) => (this.state.form!.parametersetId = val.detail.value[0])}
                      options={this.state.parameters}
                      selectionMode='single'
                    />
                  </React.Fragment>
                ))}
              {this.#panelFields.node &&
                this.state.nodes.length > 1 &&
                (panelIsReadOnly ? (
                  <SwwcInputField
                    label='Node'
                    value={this.state.form.nodeId ?? this.state.defaultNode?.label ?? ''}
                    readonly={true}
                  />
                ) : (
                  <React.Fragment>
                    <SwwcLabel label='Node' />
                    <SwwcDropdown
                      initialValue={this.state.form.nodeId ?? this.state.defaultNode?.value ?? ''}
                      onValueChange={(val) => (this.state.form!.nodeId = val.detail.value[0])}
                      options={this.state.nodes}
                      selectionMode='single'
                    />
                  </React.Fragment>
                ))}
              {this.#panelFields.template &&
                this.state.images.length > 1 &&
                (panelIsReadOnly ? (
                  <SwwcInputField
                    label='Template'
                    value={this.state.form.image ?? this.state.defaultImage?.label ?? ''}
                    readonly={true}
                  />
                ) : (
                  <React.Fragment>
                    <SwwcLabel label='Template' />
                    <SwwcDropdown
                      initialValue={this.state.form.imageId ?? this.state.defaultImage?.value ?? ''}
                      onValueChange={(val) => (this.state.form!.imageId = val.detail.value[0])}
                      options={this.state.images}
                      selectionMode='single'
                    />
                  </React.Fragment>
                ))}
              {(this.#panelFields.upload || this.state.form.id) && <SwwcLabel label='Data file' />}
              {this.#panelFields.upload &&
                (!this.state.formReadOnly || this.state.formSubmission || this.state.formUploading) &&
                (this.state.selected === undefined || this.state.selected.status === 'new') && (
                  <SwwcFileUpload
                    ref={this.#fileUpload}
                    accept='.zip'
                    method='PATCH'
                    maxFileSize={Settings.configuration.limit.file}
                    manualUpload={panelIsCreation}
                    targetUrl={Communication.resolveApi({
                      workspace: true,
                      datastore: this.#panelFields.datastore,
                      resource: this.state.form.id ?? this.state.form.name,
                    })}
                    onUploadSending={(event) => {
                      this.setState({formUploading: true});
                      if (this.state.selected) return;
                      if (this.state.form !== undefined && this.state.form.status === 'ready')
                        event.detail.formData.append('status', 'ready');
                    }}
                    onUploadError={(event) => {
                      this.setState({formUploading: false});
                      if (this.state.selected) return;
                      this.#fileUploadStatus.dispatch({
                        done: false,
                        url: event.detail.file.uploadTarget,
                      });
                    }}
                    onUploadAbort={(event) => {
                      this.setState({formUploading: false});
                      if (this.state.selected) return;
                      this.#fileUploadStatus.dispatch({
                        done: false,
                        url: event.detail.file.uploadTarget,
                      });
                    }}
                    onUploadCompleted={(event) => {
                      this.setState({formUploading: false});
                      if (this.state.selected) return;
                      this.#fileUploadStatus.dispatch({
                        done: true,
                        url: event.detail.file.uploadTarget,
                      });
                    }}
                  />
                )}
              {this.state.form.id !== undefined && (
                <div>
                  {this.state.selected === undefined ||
                  this.state.selected.dataFiles === undefined ||
                  this.state.selected.dataFiles.length === 0 ? (
                    <SwwcInputField
                      label=''
                      value='No data files available'
                      readonly={true}
                    />
                  ) : (
                    this.state.selected.dataFiles.map((ele) => (
                      <SwwcDefaultCell
                        key={`${this.state.formReadOnly} ${ele}`}
                        subTitle={ele}
                        density='compact'
                        grayish={true}
                        selected={false}
                        onSelectChanged={(event) => (event.target.selected = false)}
                      >
                        {this.state.formReadOnly ? (
                          <div slot='right-column-area'>
                            <SwwcIcon
                              name='cmdDownload16'
                              size='lg'
                              title={`Download: ${ele}`}
                              onClick={() =>
                                (window.location.href = Communication.resolveApi({
                                  workspace: true,
                                  datastore: this.#panelFields.datastore,
                                  resource: this.state.form!.id,
                                  file: ele,
                                }))
                              }
                            />
                          </div>
                        ) : (
                          this.state.selected &&
                          this.state.selected.status === 'new' && (
                            <div slot='right-column-area'>
                              <SwwcIcon
                                name='cmdTrash16'
                                size='lg'
                                title='Delete data file'
                                onClick={(event) => {
                                  event.stopPropagation();
                                  const item = this.state.form!;
                                  Communication.fetch(
                                    {
                                      workspace: true,
                                      datastore: this.#panelFields.datastore,
                                      resource: item.id ?? item.name,
                                      file: ele,
                                    },
                                    'DELETE',
                                    'json+object',
                                  ).then(
                                    (res) => {
                                      if (res.status === 'ok') {
                                        App.toast('info', this.props.type, undefined, `${ele} is deleted`);
                                      } else {
                                        App.toast(
                                          'error',
                                          this.props.type,
                                          undefined,
                                          `Failed to delete ${ele} of ${item.name} [ID: ${item.id}]: ${
                                            'data' in res
                                              ? typeof res.data === 'string'
                                                ? res.data
                                                : JSON.stringify(res.data)
                                              : res.error.message
                                          }`,
                                        );
                                      }
                                    },
                                    (val) => {
                                      App.toast(
                                        'error',
                                        this.props.type,
                                        undefined,
                                        `Unknown error during deletion ${ele} of ${item.name} [ID: ${item.id}]: ${val}`,
                                      );
                                    },
                                  );
                                }}
                              />
                            </div>
                          )
                        )}
                      </SwwcDefaultCell>
                    ))
                  )}
                </div>
              )}
              {this.#panelFields.upload &&
                this.state.form.id !== undefined &&
                (panelIsReadOnly ? (
                  <SwwcInputField
                    label='Selected data file'
                    value={this.state.form.selectedDataFile ?? 'n/a'}
                    readonly={true}
                  />
                ) : (
                  <React.Fragment>
                    <SwwcLabel label='Selected data file' />
                    <SwwcDropdown
                      initialValue={this.state.form.selectedDataFile ?? ''}
                      onValueChange={(val) => (this.state.form!.selectedDataFile = val.detail.value[0])}
                      options={this.state.selected!.dataFiles?.map((ele) => ({label: ele, value: ele})) ?? []}
                      selectionMode='single'
                    />
                  </React.Fragment>
                ))}
              {this.#panelFields.extraResultFiles &&
                (
                  <SwwcInputField
                    name='extraResultFiles'
                    autofocus={true}
                    label='Extra Files Pattern for Data file'
                    value={this.state.form.extraResultFiles ?? ''}
                    onValueChanged={(val) => (this.state.form!.extraResultFiles = val.detail.newValue)}
                    readonly={panelIsReadOnly}
                  />
                )
              }
              {this.#panelFields.environment && this.state.form.env.length === 0 ? (
                <SwwcInputField
                  label='Environment values'
                  value='No environment values configured'
                  readonly={true}
                />
              ) : this.state.formReadOnly && !this.state.formSubmission ? (
                <React.Fragment>
                  <SwwcLabel label='Environment values' />
                  {this.state.form.env.map((ele) => (
                    <SwwcDefaultCell
                      key={ele.key}
                      cellTitle={ele.key}
                      description={ele.value.length === 0 ? 'true' : ele.value}
                      density='compact'
                      grayish={true}
                      selected={false}
                    >
                      {this.state.selected && this.state.selected.status === 'new' && (
                        <div slot='right-column-area'>
                          <SwwcIcon
                            name='cmdTrash16'
                            size='lg'
                            title='Delete'
                            onClick={(event: {stopPropagation: () => void}) => {
                              event.stopPropagation();
                              Communication.fetch(
                                {
                                  workspace: true,
                                  datastore: this.#panelFields.datastore,
                                  resource: this.state.form!.id,
                                },
                                'PATCH',
                                'json+object',
                                {removeEnvironment: [ele.key]},
                              ).then(
                                (res) => {
                                  if (res.status !== 'ok')
                                    App.toast(
                                      'error',
                                      this.props.type,
                                      undefined,
                                      `Failed to delete environment key "${ele.key}" for ${this.props.type} ${
                                        this.state.form!.name
                                      } [ID: ${this.state.form!.id}]: ${
                                        'data' in res
                                          ? typeof res.data === 'string'
                                            ? res.data
                                            : JSON.stringify(res.data)
                                          : res.error.message
                                      }`,
                                    );
                                },
                                (val) => {
                                  App.toast(
                                    'error',
                                    this.props.type,
                                    undefined,
                                    `Unknown error during deletion of environment key "${ele.key}" for ${this.props.type} ${
                                      this.state.form!.name
                                    } [ID: ${this.state.form!.id}]: ${val}`,
                                  );
                                },
                              );
                            }}
                          ></SwwcIcon>
                        </div>
                      )}
                    </SwwcDefaultCell>
                  ))}
                </React.Fragment>
              ) : (
                this.state.form.env.map((ele) => (
                  <SwwcInputField
                    key={ele.key}
                    label={`Environment: ${ele.key}`}
                    value={ele.value}
                    readonly={panelIsReadOnly}
                    onValueChanged={(val) => {
                      ele.changed = true;
                      ele.value = val.detail.newValue;
                    }}
                  />
                ))
              )}
              {this.#panelFields.environment &&
                (!this.state.formReadOnly || this.state.formSubmission) &&
                (this.state.selected === undefined || this.state.selected.status === 'new') && (
                  <React.Fragment>
                    <SwwcButton
                      label='Add environment value'
                      onClickEvent={() => {
                        this.state.form!.newEnv.push({key: '', value: '', index: this.state.form!.newEnv.length});
                        this.forceUpdate();
                      }}
                    />
                    {this.state.form.newEnv.map((ele) => (
                      <React.Fragment key={ele.index}>
                        <SwwcLabel label='New environment' />
                        <SwwcInputField
                          label=''
                          placeholder='key'
                          errorMessage='Key is required'
                          required
                          minlength={1}
                          onValueChanged={(val) => (ele.key = val.detail.newValue)}
                          readonly={this.state.formReadOnly}
                        />
                        <SwwcInputField
                          label=''
                          placeholder='value'
                          onValueChanged={(val) => (ele.value = val.detail.newValue)}
                          readonly={this.state.formReadOnly}
                        />
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                )}
              {this.#panelFields.template && this.state.formReadOnly && !this.state.formSubmission && (
                <React.Fragment>
                  <SwwcInputField
                    name='execId'
                    label='Execution identifier'
                    value={this.state.form.execId ?? ''}
                    readonly={true}
                  />
                  <SwwcInputField
                    name='startTime'
                    label='Execution start time'
                    value={this.state.form.startTime ?? ''}
                    readonly={true}
                  />
                  <SwwcInputField
                    name='endTime'
                    label='Execution end time'
                    value={this.state.form.endTime ?? ''}
                    readonly={true}
                  />
                </React.Fragment>
              )}
              {this.state.form.custom !== undefined && (
                <React.Fragment>
                  <SwwcLabel label='Custom' />
                  <Information
                    item={this.state.form.custom as null}
                    map={{}}
                  />
                </React.Fragment>
              )}
            </Panel>
          )}
        </div>
      </React.Fragment>
    );
  }
}
